import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useFormik } from 'formik'
import { isNaN, map, round, trim } from 'lodash'
import * as React from 'react'
import { useEffect, useState } from 'react'
import * as yup from 'yup'
import Gift3DIcon from '../../../../assets/icons/Gift3D.svg?react'
import { useProfileStore } from '../../../../store/storeProfile'

const validationSchema = yup.object({
    amount: yup
        .number()
        .required('Введите сумму пополнения')
        .typeError('Сумма пополнения может состоять только из цифр')
        .min(100, 'Сумма пополнения должна быть больше 100₽')
        .max(100000, 'Сумма пополнения не может быть больше 100 000₽'),
})

const ButtonWithSum = ({ sum, click }) => {
    return (
        <Button
            onClick={() => click()}
            variant="outlined"
            sx={{
                padding: '8px 12px 8px 12px',
                fontWeight: '500',
            }}>
            {sum} ₽
        </Button>
    )
}

export const BalanceForm: React.FC = () => {
    const { deposit, isLoadingDeposit } = useProfileStore((state) => state)

    const [sum, setSum] = useState(0)

    const {
        handleSubmit,
        values,
        touched,
        handleBlur,
        handleChange,
        errors,
        isValid,
        dirty,
        setValues,
        resetForm,
    } = useFormik({
        initialValues: {
            amount: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const { amount } = values
            await deposit({ amount: trim(amount, ' ') })
            await resetForm()
        },
    })

    useEffect(() => {
        if (isNaN(+values.amount)) {
            setSum(0)
        } else {
            setSum(+values.amount)
        }
    }, [values.amount])

    return (
        <Card sx={{ borderRadius: '24px', border: '1px solid #E9EAED', p: '16px' }}>
            <form onSubmit={handleSubmit}>
                <Grid
                    container
                    sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                    spacing={2}>
                    <Grid size={{ sm: 12, md: 7 }}>
                        <Typography variant="h5" sx={{ textAlign: 'left' }}>
                            С карты
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{ textAlign: 'left', color: '#5A5F72' }}
                            my={2}>
                            Введите сумму от 100 ₽ до 100 000 ₽
                        </Typography>

                        <Chip
                            label="1 ₽ = 1 коин"
                            sx={{
                                background: '#2E3038',
                                color: '#fff',
                                fontWeight: '500',
                                p: '6px 12px',
                                borderRadius: '24px',
                            }}
                        />

                        <TextField
                            required
                            sx={{ marginBottom: '16px' }}
                            margin="normal"
                            fullWidth
                            id="amount"
                            name="amount"
                            placeholder="Введите сумму"
                            value={values.amount}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.amount && Boolean(errors.amount)}
                            helperText={touched.amount && errors.amount}
                        />

                        <Grid
                            container
                            spacing={1}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                marginBottom: '24px',
                            }}>
                            {map([500, 1000, 2000, 3000, 5000], (value, index) => {
                                return (
                                    <ButtonWithSum
                                        key={`${value}_${index}`}
                                        sum={value}
                                        click={() =>
                                            setValues({
                                                amount: value,
                                            })
                                        }
                                    />
                                )
                            })}
                        </Grid>
                    </Grid>
                    <Grid size={{ sm: 12, md: 5 }}>
                        <Card
                            sx={{
                                p: 2,
                                background: '#F9FAFB',
                                border: '1px solid #F4F4F6',
                                borderRadius: '24px',
                            }}>
                            <Stack
                                spacing={2}
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background:
                                        'linear-gradient( 0deg, rgba(191, 225, 255, 0.4) 0%, rgba(143, 223, 255, 0.4) 55%, rgba(194, 221, 255, 0.4) 100% )',
                                    border: '2px solid #74B9FF',
                                    borderRadius: '24px',
                                    p: '14px 16px',
                                    m: 0,
                                }}>
                                <Stack direction="row" spacing={1}>
                                    <Gift3DIcon style={{ width: '16px' }} />
                                    <Typography variant="caption1">Коины в подарок</Typography>
                                </Stack>

                                <Typography variant="caption1">10%</Typography>
                            </Stack>
                            <Stack
                                spacing={2}
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    my: 2,
                                }}>
                                <Typography sx={{ color: '#5A5F72' }} variant="body2">
                                    К получению:
                                </Typography>
                                <Typography sx={{ color: '#181B25' }} variant="caption1">
                                    {isValid && dirty ? round(sum + sum / 10) : 0} коинов
                                </Typography>
                            </Stack>

                            <Button
                                disabled={!(isValid && dirty) || isLoadingDeposit}
                                type="submit"
                                fullWidth
                                variant="contained">
                                Пополнить {isValid ? `на ${sum} ₽` : ''}
                            </Button>
                            <Typography
                                sx={{ color: '#5A5F72', textAlign: 'center', mt: 2 }}
                                variant="caption2"
                                component="p">
                                При пополнении на сумму тарифа или выше, тариф активируется
                                автоматически
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>
            </form>
        </Card>
    )
}
